import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChangeLog, Pagination, Response } from '@ui/src';
import { Blacklist, BlacklistSettings, BlacklistData } from '../../../types';
import { blacklistApi } from '../services';

export interface BlacklistPageState {
  blacklist?: Blacklist;
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  template?: string;
  uploadedRows?: number;
  values?: BlacklistData[];
  pagination?: Pagination;
  skipValue?: number;
  selectedListIds?: string[];
  blacklistChangeLogs?: ChangeLog[];
  blacklistChangeLogsPagination?: Pagination;
}

const initialState: BlacklistPageState = {};

export const blacklistPageSlice = createSlice({
  name: 'blacklistPage',
  initialState,
  reducers: {
    onBlacklistUpdate: (state, action: PayloadAction<Blacklist>) => {
      state.blacklist = action.payload;
    },
    setSelectedListIds: (state, action: PayloadAction<string[]>) => {
      state.selectedListIds = action.payload;
    },
    setSkipValue: (state, action: PayloadAction<number>) => {
      state.skipValue = action?.payload;
    },
    clearChangeLogs: (state) => {
      state.blacklistChangeLogs = undefined;
      state.blacklistChangeLogsPagination = undefined;
    },
    clearBlacklistState: (state) => {
      state.blacklist = undefined;
      state.skipValue = undefined;
      state.values = undefined;
      state.pagination = undefined;
      state.selectedListIds = undefined;
      state.blacklistChangeLogs = undefined;
      state.blacklistChangeLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        blacklistApi.endpoints.getBlacklistTemplate.matchFulfilled,
        (state, action: PayloadAction<any>) => {
          state.template = action.payload as string;
        },
      )
      .addMatcher(
        blacklistApi.endpoints.getBlacklist.matchFulfilled,
        (state, action: PayloadAction<{ data: Blacklist }>) => {
          state.blacklist = action.payload.data;
        },
      )
      .addMatcher(
        blacklistApi.endpoints.toggleBlacklistStatus.matchFulfilled,
        (state, action: PayloadAction<{ data: { active: boolean } }>) => {
          const nextBlacklist = { ...state.blacklist };

          nextBlacklist.active = action.payload.data.active;
          state.blacklist = nextBlacklist as Blacklist;
        },
      )
      .addMatcher(
        blacklistApi.endpoints.updateBlacklist.matchFulfilled,
        (state, action: PayloadAction<{ data: Blacklist }>) => {
          const nextBlacklist = { ...state.blacklist };
          nextBlacklist.name = action.payload.data.name;
          nextBlacklist.description = action.payload.data.description;
          nextBlacklist.active = action.payload.data.active;

          state.blacklist = nextBlacklist as Blacklist;
        },
      )
      .addMatcher(
        blacklistApi.endpoints.updateBlacklistSettings.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: BlacklistSettings & {
              id: string;
              blacklistId: string;
            };
          }>,
        ) => {
          const nextBlacklist = { ...state.blacklist };

          nextBlacklist.settings = action.payload.data;

          state.blacklist = nextBlacklist as Blacklist;
        },
      )
      .addMatcher(
        blacklistApi.endpoints.uploadBlacklistData.matchFulfilled,
        (state, action: PayloadAction<any>) => {
          state.uploadedRows = action.payload.data.data;
        },
      )
      .addMatcher(
        blacklistApi.endpoints.getBlacklistData.matchFulfilled,
        (state, action: PayloadAction<Response<BlacklistData>>) => {
          state.values = action?.payload?.data?.edges?.map((item) => ({
            ...item.node,
            source: item?.node?.source,
            createdAt: item?.node?.source?.createdAt,
            lastUpdated: item?.node?.source?.lastUpdated,
            name:
              item?.node?.source?.firstName || item?.node?.source?.lastName
                ? `${item?.node?.source?.firstName || ''} ${
                    item?.node?.source?.lastName || ''
                  } ${item?.node?.source?.middleName || ''}`
                : item?.node?.source?.name || '-',
            blacklistId: item?.node?.source?.blacklistId,
          }));
          state.pagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        blacklistApi.endpoints.getBlacklistsChangeLogs.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<ChangeLog>> & {
            meta: { arg: { originalArgs: string } };
          },
        ) => {
          if (!action.meta.arg.originalArgs.includes('cursor')) {
            state.blacklistChangeLogs = action.payload.data?.edges?.map(
              (item) => item.node,
            );
          } else {
            const nextChangeLogs = ([] as ChangeLog[]).concat(
              state.blacklistChangeLogs as ChangeLog[],
            );

            action.payload.data.edges?.forEach((item) => {
              nextChangeLogs.push(item.node);
            });

            state.blacklistChangeLogs = nextChangeLogs;
          }

          state.blacklistChangeLogsPagination = action.payload.data?.pageInfo;
        },
      );
  },
});

export const {
  clearBlacklistState,
  onBlacklistUpdate,
  setSkipValue,
  setSelectedListIds,
  clearChangeLogs,
} = blacklistPageSlice.actions;

export default blacklistPageSlice.reducer;
